import React, { useState, useCallback, useMemo } from 'react';
import * as Styled from './MListIconGroup.styles';
import type { MListIconGroupProps } from './MListIconGroup.types';
import { AIcon, AIconSize } from '../../atoms/AIcon';
import { ASubheader, ASubheaderSize } from '../../atoms/ASubheader';
import { MTextBlockInteractive } from '../MTextBlockInteractive';
import { ALinkStandalone, ALinkStandaloneSize } from '../../atoms/ALinkStandalone';
import { DEFAULT_SEE_MORE_TEXT, DEFAULT_SEE_LESS_TEXT } from '../../utils/i18nConstants';

const COMPONENT_NAME = 'm-web-ui-kit-MListIconGroup';
const TEST_ID = 'web-ui-kit-MListIconGroup';

const DEFAULT_TRUNCATE_AFTER = 5;

export function MListIconGroup({
  aIcon,
  aSubheader,
  listItems = [],
  truncated = false,
  truncateAfter = DEFAULT_TRUNCATE_AFTER,
  seeMoreText = DEFAULT_SEE_MORE_TEXT,
  seeLessText = DEFAULT_SEE_LESS_TEXT,
  onToggle,
  className,
  ...rest
}: MListIconGroupProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const shouldShowToggle = truncated && listItems.length > truncateAfter;

  const visibleItems = useMemo(() => {
    if (!truncated || isExpanded) {
      return listItems;
    }
    return listItems.slice(0, truncateAfter);
  }, [listItems, truncated, isExpanded, truncateAfter]);

  const handleToggle = useCallback(() => {
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);
    onToggle?.(newExpandedState);
  }, [isExpanded, onToggle]);

  const hasIcon = Boolean(aIcon?.icon);
  const hasSubheader = Boolean(aSubheader?.text?.trim());

  if (!hasSubheader && listItems.length === 0) {
    return null;
  }

  return (
    <Styled.Wrapper data-component-name={COMPONENT_NAME} data-testid={TEST_ID} className={className} {...rest}>
      {hasIcon && (
        <Styled.IconWrapper>
          <AIcon size={aIcon?.size ?? AIconSize.L} icon={aIcon?.icon ?? ''} {...aIcon} />
        </Styled.IconWrapper>
      )}
      <Styled.ContentContainer>
        {hasSubheader && (
          <Styled.SubheaderWrapper>
            <ASubheader text={aSubheader.text} size={aSubheader.size ?? ASubheaderSize.L} />
          </Styled.SubheaderWrapper>
        )}
        {visibleItems.length > 0 && (
          <Styled.ListItemsContainer>
            {visibleItems.map((item, index) => (
              <MTextBlockInteractive key={index} {...item} />
            ))}
          </Styled.ListItemsContainer>
        )}
        {shouldShowToggle && (
          <Styled.LinkWrapper>
            <ALinkStandalone
              text={isExpanded ? seeLessText : seeMoreText}
              size={ALinkStandaloneSize.Small}
              onClick={handleToggle}
              aria-expanded={isExpanded}
            />
          </Styled.LinkWrapper>
        )}
      </Styled.ContentContainer>
    </Styled.Wrapper>
  );
}

export { TEST_ID as M_LIST_ICON_GROUP_TEST_ID };
export * from './MListIconGroup.types';
